import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import { FeatureAppDefinition, FeatureServices } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { CarConfigurationServiceV2 } from '@volkswagen-onehub/car-configuration-feature-service';
import { ApiKeyProviderV1 } from '@volkswagen-onehub/api-key-provider';
import { DisclaimerManagerV1 } from '@volkswagen-onehub/disclaimer-manager';
import { LayerManagerV2 } from '@volkswagen-onehub/layer-manager';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { PageInfoServiceV1 } from '@volkswagen-onehub/page-info-service';
import { ServiceConfigProviderV1, ServiceConfigurationV1 } from '@volkswagen-onehub/service-config-provider';
import { TrackingManagerV1 } from '@volkswagen-onehub/tracking-service';
import * as React from 'react';
import { handleOffers } from 'src/bff';
import { OffersApp } from './App';
import { OffersState } from './redux/reducer';
import { FeatureAppEnvironmentContext } from 'src/feature-app/hooks';
import { Router } from 'react-router';
import { HistoryServiceV2 } from '@feature-hub/history-service';
import { FaContentServiceV1 } from '@volkswagen-onehub/fa-content-service';
import { FeatureappServiceV1 } from '@volkswagen-onehub/featureapp-service';
import { saveFeatureServices } from 'src/feature-app';
import ErrorBoundary from './sentry/ErrorBoundary';
import { initSentry } from './sentry';

export type OffersMode = 'default' | 'filter' | 'carousel';
export type OffersFormularios = 'pre' | 'pro';
export type OffersCTA = 'oferta' | 'call_me_now' | undefined;
export interface Promotion {
  bodycopy: string;
  anchor: string;
  ctaTitle: string;
}

export interface OffersFeatureAppConfig {
  readonly mode?: OffersMode;
  readonly mainPage?: {
    N: string;
    V: string;
  };
  readonly env?: { formulario?: OffersFormularios };
  readonly promociones?: { 'moves-3'?: Promotion; 'gama-mas'?: Promotion; [key: string]: Promotion }[];
}

export interface Dependencies extends FeatureServices {
  readonly 's2:async-ssr-manager': AsyncSsrManagerV1 | undefined;
  readonly 's2:serialized-state-manager': SerializedStateManagerV1;
  readonly 'disclaimer-manager': DisclaimerManagerV1;
  readonly 'car-configuration': CarConfigurationServiceV2;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'service-config-provider': ServiceConfigProviderV1;
  readonly 'layer-manager': LayerManagerV2;
  readonly 's2:history': HistoryServiceV2;
  readonly tracking?: TrackingManagerV1;
  readonly 'fa-content-service': FaContentServiceV1;
  readonly 'featureapp-service': FeatureappServiceV1;
  readonly 'page-info-service': PageInfoServiceV1;
  readonly 'api-key-provider': ApiKeyProviderV1;

  // readonly 'car-configuration': CarConfigurationServiceV1;
}

async function fetchOffersState(faContent: FaContentServiceV1, dataService: ServiceConfigurationV1): Promise<string> {
  try {
    const data = await handleOffers(faContent.content, dataService);
    return JSON.stringify(data);
  } catch (error) {
    throw new Error('Error fetching carlines');
  }
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, Dependencies, OffersFeatureAppConfig> = {
  dependencies: {
    featureServices: {
      's2:serialized-state-manager': '^1.0.0',
      'disclaimer-manager': '^1.1.0',
      'car-configuration': '^2.0.0',
      'locale-service': '^1.0.0',
      'service-config-provider': '^1.0.0',
      'layer-manager': '^2.7.2',
      's2:history': '^2.0.0',
      'fa-content-service': '^1.0.0',
      'featureapp-service': '^1.3.0',
      'page-info-service': '^1.1.0',
    },
  },

  optionalDependencies: {
    featureServices: {
      's2:async-ssr-manager': '^1.0.0',
      tracking: '^1.0.0',
      'api-key-provider': '^1.0.0',
    },
  },

  create: ({ config = {}, baseUrl, featureServices, featureAppId }) => {
    let initialData: OffersState = { offers: [] };
    let initialState = JSON.stringify(initialData);

    const env = { baseUrl, config, featureServices, featureAppId };

    const historyService = env.featureServices['s2:history'];
    const {
      'api-key-provider': apiKeyProvider,
      's2:async-ssr-manager': asyncSsrManager,
      's2:serialized-state-manager': serializedStateManager,
      'fa-content-service': faContent,
      'service-config-provider': serviceConfig,
    } = featureServices;

    // Necesario para que cualquier función pueda acceder a los featureServices sin usar hooks, especialmente
    // para acceder a los service configs.
    saveFeatureServices(env.featureServices);
    const { hub } = initSentry(featureServices, config);

    const data = serviceConfig.configs['es-vwdata'] as ServiceConfigurationV1;

    // We use the presence of the asyncSsrManager to determine whether we are
    // rendered on the server or on the client.
    if (asyncSsrManager) {
      //server side rendering
      serializedStateManager.register(() => initialState);

      asyncSsrManager.scheduleRerender((async () => (initialState = await fetchOffersState(faContent, data)))());
    } else {
      //client side rendering
      const serializedState = serializedStateManager.getSerializedState();
      if (serializedState) {
        initialState = serializedState;
      }
    }

    return {
      render: () => (
        <ErrorBoundary hub={hub}>
          <FeatureAppEnvironmentContext.Provider value={env}>
            <Router history={historyService.history}>
              <OffersApp initialState={JSON.parse(initialState)} config={config} />
            </Router>
          </FeatureAppEnvironmentContext.Provider>
        </ErrorBoundary>
      ),
    };
  },
};

export default featureAppDefinition;
